import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
} from "@angular/core";
import { DecimalPipe } from "@angular/common";
import { SharedModule } from "../../../../shared/shared.module";
import { ChipsComponent } from "../../../../shared/modules/chips/chips.component";
import { HelpTipComponent } from "../../../../shared/components/help-tip/help-tip.component";
import { PrepreparationService } from "../../../prepreparation/services/prepreparation.service";

/**
 * Vue résumée de la ventilation stock d'un lot — refonte fiche lot
 * (Phase 9 — Q23D du design refonte prépa physique).
 *
 * Affiche en barre segmentée 4 catégories :
 *   - stock principal (stock courant utilisable)
 *   - engagé prépa (zone-prepa-CC, ventilation par CC dans tooltip)
 *   - sur palettes EN_PREPARATION/PRETE
 *   - expédié (cumul historique)
 *
 * À placer en haut de la fiche lot, complète la liste plate des stocks
 * par adresse déjà rendue par `LotStockSummaryComponent`.
 */
@Component({
  selector: "vl-lot-prepa-summary",
  standalone: true,
  imports: [DecimalPipe, SharedModule, ChipsComponent, HelpTipComponent],
  templateUrl: "./lot-prepa-summary.component.html",
  styleUrl: "./lot-prepa-summary.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LotPrepaSummaryComponent {
  private service = inject(PrepreparationService);

  lotId = input.required<string>();
  unite = input<string | null>(null);

  loading = signal(false);
  breakdown = signal<{
    lotId: string;
    stockPrincipal: number;
    engagePrepa: number;
    surPalette: number;
    expedie: number;
    quantiteTotale: number;
    parZonePrepa: { commandeClientId: string | null; quantite: number }[];
    parPalette: { paletteId: string; quantite: number }[];
  } | null>(null);

  /** Référence (max(quantiteTotale, somme tous segments)) pour normaliser les %. */
  reference = computed(() => {
    const b = this.breakdown();
    if (!b) return 1;
    return Math.max(
      1,
      b.quantiteTotale + b.expedie,
      b.stockPrincipal + b.engagePrepa + b.surPalette + b.expedie,
    );
  });

  pctStock = computed(() => this._pct(this.breakdown()?.stockPrincipal ?? 0));
  pctEngage = computed(() => this._pct(this.breakdown()?.engagePrepa ?? 0));
  pctPalette = computed(() => this._pct(this.breakdown()?.surPalette ?? 0));
  pctExpedie = computed(() => this._pct(this.breakdown()?.expedie ?? 0));

  private _pct(v: number): number {
    const r = this.reference();
    if (!r) return 0;
    return Math.max(0, (v / r) * 100);
  }

  constructor() {
    effect(
      () => {
        const id = this.lotId();
        if (id) void this.refresh();
      },
      { allowSignalWrites: true },
    );
  }

  async refresh(): Promise<void> {
    const id = this.lotId();
    if (!id) return;
    this.loading.set(true);
    try {
      const data = await this.service.getLotBreakdown(id);
      this.breakdown.set(data);
    } catch {
      this.breakdown.set(null);
    } finally {
      this.loading.set(false);
    }
  }
}
